import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Grid,
  CardActions,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  Link as LinkIcon,
  Phone as PhoneIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
  Visibility as PreviewIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  createTemplate,
  deleteTemplate,
  getAllTemplates,
} from "../../../services/settings.service";
import {
  LANGUAGES,
  TEMPLATE_CATEGORIES,
  BUTTON_TYPES,
  HEADER_TYPES,
} from "../../../utilities/data.utility";
import {
  TemplateButton,
  WhatsAppTemplate,
} from "../../../models/whatsapp.model";
import {
  getTemplateHeaderContent,
  getTemplateHeaderType,
} from "../../../utilities/whatsappTemplate.utility";
import BusinessSettingsContext from "../../Context/Settings/BusinessSettingsContext";
import useAuthUser from "../../hooks/useAuthUser";

const TEMPLATE_STATUS = {
  PENDING: {
    color: "warning" as const,
    icon: <PendingIcon />,
    label: "Pending Review",
  },
  APPROVED: {
    color: "success" as const,
    icon: <ApprovedIcon />,
    label: "Approved",
  },
  REJECTED: {
    color: "error" as const,
    icon: <RejectedIcon />,
    label: "Rejected",
  },
  PAUSED: { color: "info" as const, icon: <ApprovedIcon />, label: "Paused" },
};

const WhatsAppTemplateTab: React.FC = () => {
  const { user } = useAuthUser();
  const { settings } = useContext(BusinessSettingsContext);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<WhatsAppTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] =
    useState<WhatsAppTemplate | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isWhatsAppConnected, setIsWhatsAppConnected] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

  // Form state
  const [templateName, setTemplateName] = useState("");
  const [category, setCategory] = useState("UTILITY");
  const [language, setLanguage] = useState("en");
  const [headerType, setHeaderType] = useState("NONE");
  const [headerContent, setHeaderContent] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [buttons, setButtons] = useState<TemplateButton[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchAllTemplates = useCallback(async () => {
    try {
      setIsLoadingTemplates(true);
      const templates = await getAllTemplates(user?.uid!);
      setTemplates(templates ?? []);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchAllTemplates();
    // Check if WhatsApp is connected by looking at window._meraaiSignup
    setIsWhatsAppConnected(!!settings?.fullPhoneNumber);
  }, [fetchAllTemplates, settings?.fullPhoneNumber]);

  const handleOpenDialog = (template: WhatsAppTemplate | null = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateName(template?.name);
      setCategory(template?.category);
      setLanguage(template?.language);
      setHeaderType(getTemplateHeaderType(template) || "NONE");
      setHeaderContent(getTemplateHeaderContent(template));
      setBodyText(template?.body);
      setFooterText(template?.footer || "");
      setButtons(template?.buttons || []);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setCategory("UTILITY");
    setLanguage("en");
    setHeaderType("NONE");
    setHeaderContent("");
    setBodyText("");
    setFooterText("");
    setButtons([]);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!templateName.trim())
      newErrors.templateName = "Template name is required";
    else if (!/^[a-z0-9_]+$/.test(templateName))
      newErrors.templateName =
        "Only lowercase letters, numbers, and underscores allowed";

    if (!bodyText.trim()) newErrors.bodyText = "Body text is required";
    else if (bodyText.length > 1024)
      newErrors.bodyText = `Body exceeds 1024 characters (${bodyText.length}/1024)`;

    if (headerType === "TEXT" && headerContent.length > 60)
      newErrors.headerContent = `Header exceeds 60 characters (${headerContent.length}/60)`;

    if (footerText.length > 60)
      newErrors.footerText = `Footer exceeds 60 characters (${footerText.length}/60)`;

    const floatingParamRegex = /^(\{\{\d+\}\}\s*)+$/;
    if (floatingParamRegex.test(bodyText.trim()))
      newErrors.bodyText =
        "Template cannot contain only variables. Add descriptive text.";

    // URL button validation
    buttons.forEach((btn, idx) => {
      if (btn.type === "URL") {
        if (!btn.url || !btn.url.trim()) {
          newErrors[`button_url_${idx}`] = "URL is required";
        } else {
          try {
            new URL(btn.url);
          } catch {
            newErrors[`button_url_${idx}`] = "Invalid URL format";
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const afterSaveCallback = (createdTemplate: any) => {
    setTemplates((previewTemplates) => [createdTemplate, ...previewTemplates]);
  };

  const onSave = async (payload: WhatsAppTemplate) => {
    try {
      const response = await createTemplate(user?.uid!, payload);
      afterSaveCallback({
        ...payload,
        id: response["id"],
      } as WhatsAppTemplate);
    } catch (e) {
      console.log(e);
    }
  };

  const handleSaveTemplate = async () => {
    if (!validateForm()) return;

    try {
      setIsSavingTemplate(true);

      const newTemplate: WhatsAppTemplate = {
        name: templateName,
        value: bodyText,
        category,
        language,
        status: editingTemplate?.status || "PENDING",
        header: { type: headerType, content: headerContent },
        body: bodyText,
        footer: footerText,
        buttons,
      };

      let updatedTemplates: WhatsAppTemplate[];
      if (editingTemplate) {
        updatedTemplates = templates.map((t) =>
          t.id === editingTemplate.id ? newTemplate : t
        );
      } else {
        updatedTemplates = [...templates, newTemplate];
      }

      setTemplates(updatedTemplates);
      await onSave(newTemplate);
    } catch (e) {
      console.log(e);
    } finally {
      setIsSavingTemplate(false);
    }
    setDialogOpen(false);
    resetForm();
  };

  const handleDeleteTemplate = async (id: string, name: string) => {
    try {
      setIsDeletingTemplate(true);
      await deleteTemplate(user?.uid!, id, name);
      setTemplates(templates.filter((t) => t.id !== id && t.name !== name));
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    } catch (e) {
      console.log(e);
      alert(`Unable to delete template ${name}. Please try again`);
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setTemplateToDelete({ id, name });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (templateToDelete) {
      handleDeleteTemplate(templateToDelete.id, templateToDelete.name);
    }
  };

  const handleAddButton = (type: string) => {
    const buttonType = BUTTON_TYPES.find((bt: any) => bt.value === type);
    const existingCount = buttons.filter((b: any) => b.type === type).length;

    if (buttonType && existingCount >= buttonType.max) {
      alert(`Maximum ${buttonType.max} ${buttonType.label} button(s) allowed`);
      return;
    }

    setButtons([...buttons, { type, text: "", url: "", phone: "" }]);
  };

  const handleUpdateButton = (index: number, field: string, value: string) => {
    const newButtons = [...buttons];
    (newButtons[index] as any)[field] = value;
    setButtons(newButtons);
  };

  const handleRemoveButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handlePreview = (template: WhatsAppTemplate) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const renderPreview = () => {
    if (!previewTemplate) return null;

    const previewHeaderType = getTemplateHeaderType(previewTemplate);
    const previewHeaderContent = getTemplateHeaderContent(previewTemplate);

    const sampleData: Record<string, string> = {
      "{{1}}": "John Doe",
      "{{2}}": "12345",
      "{{3}}": "Dec 25, 2024",
    };

    const replaceVariables = (text: string | undefined) => {
      if (!text) return text;
      let result = text;
      Object.keys(sampleData).forEach((key) => {
        result = result.replace(
          new RegExp(key.replace(/[{}]/g, "\\$&"), "g"),
          sampleData[key]
        );
      });
      return result;
    };

    return (
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          bgcolor: "#e5ddd5",
          borderRadius: 2,
          maxWidth: 400,
          width: "100%",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: 1,
          }}
        >
          {previewHeaderType && previewHeaderType !== "NONE" && (
              <Box sx={{ mb: 2 }}>
                {previewHeaderType === "TEXT" && (
                  <Typography variant="h6" fontWeight={700}>
                    {replaceVariables(previewHeaderContent)}
                  </Typography>
                )}
                {["IMAGE", "VIDEO", "DOCUMENT"].includes(previewHeaderType) && (
                  <Box
                    sx={{
                      height: 150,
                      bgcolor: "grey.200",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {previewHeaderType === "IMAGE" && (
                      <ImageIcon sx={{ fontSize: 48 }} />
                    )}
                    {previewHeaderType === "VIDEO" && (
                      <VideoIcon sx={{ fontSize: 48 }} />
                    )}
                    {previewHeaderType === "DOCUMENT" && (
                      <DocumentIcon sx={{ fontSize: 48 }} />
                    )}
                  </Box>
                )}
              </Box>
            )}

          <Typography sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
            {replaceVariables(previewTemplate.body)}
          </Typography>

          {previewTemplate.footer && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              {previewTemplate.footer}
            </Typography>
          )}

          {previewTemplate.buttons && previewTemplate.buttons.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {previewTemplate.buttons.map((btn, idx) => (
                <Button
                  key={idx}
                  variant="outlined"
                  fullWidth
                  size="small"
                  startIcon={
                    btn.type === "URL" ? (
                      <LinkIcon />
                    ) : btn.type === "PHONE_NUMBER" ? (
                      <PhoneIcon />
                    ) : (
                      <CopyIcon />
                    )
                  }
                >
                  {btn.text || `Button ${idx + 1}`}
                </Button>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={{ xs: 2, sm: 0 }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              textAlign: "left",
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            WhatsApp Message Templates
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "left" }}
          >
            Create and manage pre-approved templates for WhatsApp Business API
          </Typography>
        </Box>
        <Tooltip
          title={isWhatsAppConnected ? "" : "Connect WhatsApp account first"}
          disableHoverListener={isWhatsAppConnected}
        >
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={!isWhatsAppConnected}
            >
              Create Template
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {!isWhatsAppConnected && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Connect WhatsApp Account Required
            </Typography>
            <Typography variant="body2">
              You need to connect your WhatsApp Business Account above before
              you can create message templates.
            </Typography>
          </Box>
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={`All Templates (${templates.length})`} />
          <Tab
            label={`Approved (${
              templates.filter((t) => t.status === "APPROVED").length
            })`}
          />
          <Tab
            label={`Pending (${
              templates.filter((t) => t.status === "PENDING").length
            })`}
          />
          <Tab
            label={`Rejected (${
              templates.filter((t) => t.status === "REJECTED").length
            })`}
          />
        </Tabs>
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        {isLoadingTemplates ? (
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={48} />
                <Typography color="text.secondary">
                  Loading templates...
                </Typography>
              </Stack>
            </Box>
          </Grid>
        ) : templates.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <Typography color="text.secondary">
                No templates created yet. Click "Create Template" to get
                started.
              </Typography>
            </Box>
          </Grid>
        ) : (
          templates
            .filter(
              (t) =>
                activeTab === 0 ||
                (activeTab === 1 && t.status === "APPROVED") ||
                (activeTab === 2 && t.status === "PENDING") ||
                (activeTab === 3 && t.status === "REJECTED")
            )
            .map((template) => (
              <Grid key={template.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{ mb: 0.5, textAlign: "left" }}
                          >
                            {template.name}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Chip
                              label={template.category}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={template.language}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </Box>
                        <Chip
                          icon={TEMPLATE_STATUS[template.status].icon}
                          label={TEMPLATE_STATUS[template.status].label}
                          size="small"
                          color={TEMPLATE_STATUS[template.status].color}
                        />
                      </Stack>

                      <Divider />

                      <Box>
                        {getTemplateHeaderType(template) &&
                          getTemplateHeaderType(template) !== "NONE" && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ textAlign: "left" }}
                          >
                            Header: {getTemplateHeaderType(template)}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textAlign: "left",
                          }}
                        >
                          {template.body}
                        </Typography>
                        {template.footer && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 1, textAlign: "left" }}
                          >
                            {template.footer}
                          </Typography>
                        )}
                      </Box>

                      {template.buttons && template.buttons.length > 0 && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 0.5, textAlign: "left" }}
                          >
                            Buttons: {template.buttons.length}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ flexWrap: "wrap", gap: 0.5 }}
                          >
                            {template.buttons.map((btn, idx) => (
                              <Chip key={idx} label={btn.type} size="small" />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ mt: "auto" }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{
                        height: "100%",
                        flexWrap: "wrap",
                        gap: { xs: 0.5, sm: 1 },
                      }}
                    >
                      {" "}
                      <Button
                        size="small"
                        startIcon={<PreviewIcon />}
                        onClick={() => handlePreview(template)}
                      >
                        Preview
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          handleDeleteClick(template.id as any, template.name)
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            ))
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? "Edit Template" : "Create New Template"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              Templates must be approved by Meta before use. This typically
              takes up to 48 hours.
            </Alert>

            <TextField
              fullWidth
              label="Template Name *"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value.toLowerCase())}
              error={!!errors.templateName}
              helperText={
                errors.templateName ||
                "Use only lowercase letters, numbers, and underscores"
              }
              placeholder="order_confirmation"
            />

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  label="Category *"
                >
                  {TEMPLATE_CATEGORIES.map((cat: any) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box>
                        <Typography>{cat.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cat.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Language *</InputLabel>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  label="Language *"
                >
                  {LANGUAGES.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Divider />

            {/* Header Section */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                gutterBottom
                sx={{ textAlign: "left" }}
              >
                Header (Optional)
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
              >
                {HEADER_TYPES.map((type: any) => (
                  <Chip
                    key={type.value}
                    label={type.label}
                    onClick={() => setHeaderType(type.value)}
                    color={headerType === type.value ? "primary" : "default"}
                  />
                ))}
              </Stack>
              {headerType === "TEXT" && (
                <TextField
                  fullWidth
                  label="Header Text"
                  value={headerContent}
                  onChange={(e) => setHeaderContent(e.target.value)}
                  error={!!errors.headerContent}
                  helperText={errors.headerContent}
                  placeholder="Order Confirmation"
                />
              )}
            </Box>

            {/* Body */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Body *"
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              error={!!errors.bodyText}
              helperText={errors.bodyText}
              placeholder="Hello {{1}}, your order {{2}} has been confirmed."
            />

            {/* Footer */}
            <TextField
              fullWidth
              label="Footer"
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
              error={!!errors.footerText}
              helperText={errors.footerText}
              placeholder="Thank you for shopping with us!"
            />

            {/* Buttons */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Buttons
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {BUTTON_TYPES.map((bt: any) => (
                  <Button
                    key={bt.value}
                    size="small"
                    variant="outlined"
                    onClick={() => handleAddButton(bt.value)}
                  >
                    {bt.label}
                  </Button>
                ))}
              </Stack>

              {buttons.map((btn, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <TextField
                      label="Button Text"
                      value={btn.text}
                      onChange={(e) =>
                        handleUpdateButton(index, "text", e.target.value)
                      }
                      fullWidth
                    />
                    {btn.type === "URL" && (
                      <TextField
                        label="URL"
                        value={btn.url}
                        onChange={(e) =>
                          handleUpdateButton(index, "url", e.target.value)
                        }
                        fullWidth
                        error={!!errors[`button_url_${index}`]}
                        helperText={errors[`button_url_${index}`] || ""}
                        placeholder="https://example.com"
                      />
                    )}
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveButton(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={isSavingTemplate}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={isSavingTemplate}
            startIcon={
              isSavingTemplate ? <CircularProgress size={18} /> : undefined
            }
          >
            {isSavingTemplate ? "Saving..." : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent>{renderPreview()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Are you sure you want to delete the template
            <Box
              component="span"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {" "}
              "{templateToDelete?.name}"?
            </Box>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeletingTemplate}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeletingTemplate}
            startIcon={
              isDeletingTemplate ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }
          >
            {isDeletingTemplate ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppTemplateTab;
